import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Route, Switch, RouteComponentProps, withRouter } from 'react-router-dom';
import { TeamStore as MobxTeamStore } from 'nomst/Stores/team.store';
import SummaryDashboardView from 'nomst/Components/SummaryDashboard/summaryDashboardView';
import MobxManagerView from 'nomst/Components/Decision/decision';
import ChangeHistoryView from 'nomst/Components/ChangeHistory/changeHistoryView';
import GlobalConfigurationView from 'components/Configuration/globalConfigurationView';
import { AccountAccessDeniedView } from 'components/accountAccessDeniedView';
import CycleConfigurationsView from 'components/Configuration/cycleConfigurationsView';
import NotFoundErrorView from 'components/ErrorPage/notFoundErrorView';
import GlobalErrorView from 'components/ErrorPage/globalErrorView';
import EmployeeCompReport from 'nomst/Components/CompReport/employeeCompReport';
import HeaderNav from 'components/headerNav';
import AlertModal from 'components/Common/alertModal';
import NotificationView from 'components/Notification/notificationView';
import StatusIndicator from 'components/StatusIndicator';
import { appState } from 'singletons';
import DiagnosticsView from 'components/diagnosticsView';
import CycleSettingsView from 'nomst/Components/CycleSettings/cycleSettingsView';
import EmptyManagerView from 'nomst/Components/Decision/emptyManagerView';
import ErrorDisplayView from 'nomst/Components/Decision/errorDisplayView';
import TeamDown from 'nomst/Components/Decision/teamDown';
import { JobsListView } from 'nomst/Components/JobsListView/jobsListView';
import { JobDetailsView } from 'nomst/Components/JobDetails/jobDetailsView';
import { EmployeesListView } from 'nomst/Components/EmployeesListView/employeesListView';
import EmployeeDetailsView from 'nomst/Components/EmployeeDetails/employeeDetailsView';
import { ContactUsForm } from '@payscale/payscale-shared-ui/lib/ContactUsForm';
import { SharedSlideout } from '@payscale/payscale-shared-ui/lib/Slideout';
import { TeamRoutes, FeatureFlags } from 'typings/enums';
import { contactFormHelper, notificationStore } from 'utils/MobxDataManager/singletons';
import { featureFlagHelper } from 'singletons';
import 'regenerator-runtime/runtime';
import '../Styles/team.scss';

export interface ITeamProps {
    store: MobxTeamStore;
}

interface IRouteProps extends RouteComponentProps<ITeamProps>, React.Props<ITeamProps>, ITeamProps {
}

class Team extends React.Component<IRouteProps> {

    constructor(props: IRouteProps) {
        super(props);
        this.toggleSlideout = this.toggleSlideout.bind(this);
        this.submitFormData = this.submitFormData.bind(this);
        this.getSuggestedArticles = this.getSuggestedArticles.bind(this);
        this.goToArticle = this.goToArticle.bind(this);
    }

    renderAlertModal(): React.ReactNode {
        const { alert } = this.props.store;
        if (alert.hidden) {
            return null;
        }

        return (
            <AlertModal
                title={alert.title || ''}
                message={alert.message || ''}
                OnClose={alert.hide}
            />
        );
    }

    renderNotificationView(): React.ReactNode {
        if (!notificationStore.visible) {
            return null;
        }

        return(<NotificationView notificationStore={notificationStore} />);
    }

    renderStatusIndicator(): React.ReactNode {
        return appState ? (
            <StatusIndicator
                dataState={appState.currentDataState}
                logout={this.props.store?.logout}
            />
        ) : null;
    }

    toggleSlideout(): void {
        contactFormHelper.toggleSlideout();
    }

    getSuggestedArticles(term: string): Promise<void> {
        return contactFormHelper.getSuggestedArticles(term, true);
    }

    goToArticle(id: string): void {
        contactFormHelper.goToArticle(id);
    }

    submitFormData(formData: ContactFormData): Promise<TicketSubmissionResult> {
        return contactFormHelper.submitForm(formData);
    }

    renderContactForm(): React.ReactNode {
        const showCaseDeflection = featureFlagHelper.featureFlagSettings[FeatureFlags.ShowCaseDeflection];
        return (
            <SharedSlideout
                isOpen={contactFormHelper.isOpen}
                panelClassName="team-contact-form"
                handleCloseSlideout={this.toggleSlideout}
                useCloseButton={true}
            >
                <ContactUsForm
                    productName={'Team'}
                    pageSource={contactFormHelper.currentPage}
                    onSubmitFn={this.submitFormData}
                    onClose={this.toggleSlideout}
                    articles={contactFormHelper.salesForceArticles}
                    initialSearchTerms={contactFormHelper.salesForceSearchTerms}
                    isVisible={contactFormHelper.isOpen}
                    searchTermCallBack={this.getSuggestedArticles}
                    goToArticle={this.goToArticle}
                    showCaseDeflection={showCaseDeflection as boolean}
                />
            </SharedSlideout>
        );
    }

    render(): React.ReactNode {
        return (
            <div className={`${this.props.store.rootClasses} app-root`}>
                <HeaderNav store={this.props.store} isFullPageHelpForm={contactFormHelper.isFullPageHelpForm} />
                <Switch>
                    <Route exact={true} path={TeamRoutes.SummaryDashboard} component={SummaryDashboardView} />
                    <Route path={TeamRoutes.MobxManager} component={MobxManagerView} />
                    <Route path={TeamRoutes.GlobalConfiguration} component={GlobalConfigurationView} />
                    <Route path={TeamRoutes.CycleConfiguration} component={CycleConfigurationsView} />
                    <Route path={TeamRoutes.AccessDenied} component={AccountAccessDeniedView} />
                    <Route path={TeamRoutes.NotFound} component={NotFoundErrorView} />
                    <Route path={TeamRoutes.Error} component={GlobalErrorView} />
                    <Route path={TeamRoutes.Diagnostics} component={DiagnosticsView} />
                    <Route path={TeamRoutes.ChangeHistory} component={ChangeHistoryView}/>
                    <Route path={TeamRoutes.CycleSettings} component={CycleSettingsView} />
                    <Route path={TeamRoutes.CycleNotOpenManagerView} component={EmptyManagerView} />
                    <Route path={TeamRoutes.AccountError} component={ErrorDisplayView} />
                    <Route path={TeamRoutes.TeamDown} component={TeamDown} />
                    <Route path={TeamRoutes.JobsList} component={JobsListView} />
                    <Route path={TeamRoutes.JobDetails} component={JobDetailsView} />
                    <Route path={TeamRoutes.EmployeesList} component={EmployeesListView} />
                    <Route path={TeamRoutes.EmployeeDetailsView} component={EmployeeDetailsView} />
                    <Route path={TeamRoutes.Print} component={EmployeeCompReport} />
                    <Route exact={true} path={''} component={SummaryDashboardView} />
                </Switch>
                {this.renderAlertModal()}
                {this.renderNotificationView()}
                {this.renderStatusIndicator()}
                {this.renderContactForm()}
            </div>
        );
    }
}

export default withRouter(inject('store')(observer(Team)));